import { useEffect, useState } from 'react';
import {
	getCachedProduct,
	getProductById,
	ProductApiError,
} from '../services/productApi';
import type { ProductResponse } from '../services/productApi';

interface ProductDataState {
	product: ProductResponse | null;
	isLoading: boolean;
	error: string | null;
}

function stateForProduct(productId: string): ProductDataState {
	const cached = getCachedProduct(productId);
	return cached
		? { product: cached, isLoading: false, error: null }
		: { product: null, isLoading: true, error: null };
}

export function useProductData(productId: string): ProductDataState {
	const [state, setState] = useState<ProductDataState>(() =>
		stateForProduct(productId),
	);
	const [trackedId, setTrackedId] = useState(productId);

	if (trackedId !== productId) {
		setTrackedId(productId);
		setState(stateForProduct(productId));
	}

	useEffect(() => {
		// Cache hit was already resolved synchronously above; no fetch needed.
		if (getCachedProduct(productId)) return;

		let mounted = true;

		getProductById(productId)
			.then((product) => {
				if (mounted) {
					setState({ product, isLoading: false, error: null });
				}
			})
			.catch((err) => {
				if (!mounted) return;

				let message = 'Failed to load product';
				if (err instanceof ProductApiError && err.status === 404) {
					message = 'Product not found';
				} else if (err instanceof Error) {
					message = err.message;
				}

				setState({ product: null, isLoading: false, error: message });
			});

		return () => {
			mounted = false;
		};
	}, [productId]);

	return state;
}
